import React, { useContext, useEffect, useState } from "react";
import TableCustom from "../../../../../../../components/Table";
import { LocalizationContext } from "../../../../../../../locales/Translation";
import SizeBox from "../../../../../../../components/SizeBox";
import {
  dashboardFilterDateRangeSelector,
  dashboardPropertyChooseSelector,
  dashboardDayOfficeBookingFilterSelector,
  dashboardDayOfficeBookingSelector,
  filterListPropertySelectedSelector,
} from "../../../../../selectors/DashboardSelectors";
import { useDispatch, useSelector } from "react-redux";
import { DayOfficeBookingType } from "../../../../../configs/DeclareTypes";
import {
  dashboardDayOfficeBookingChangeStatusAction,
  dashboardDayOfficeBookingFilterAction,
} from "../../../../../actions/DashboardActions";
import { dashboardDayOfficeBookingPropertyThunk } from "../../../../../thunks/DashboardThunks";
import { userCompanyChooseSelector } from "../../../../../../User/selectors/UserSelectors";
import {
  ORDER_BY_ASC,
  ORDER_BY_DESC,
  ORDER_BY_NONE,
} from "../../../../../../App/configs/Constants";
import BookingAction from "../BookingAction";
import { usePermissionCheck } from "../../../../../../../utils/custom-hooks";
import { UI_ACCESS_TRANSACTION_BY_FILTER_PER } from "../../../../../../../utils/PermissionConstant";
import { DAY_OFFICE_BOOKING } from "../../../../../configs/Constants";
import ReadMore from "../../../../../../../components/ReadMore";

const TableDayOfficeBookings = () => {
  const { translations } = useContext(LocalizationContext);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const dashboardDayOfficeBooking = useSelector(
    dashboardDayOfficeBookingSelector
  );
  const dashboardDayOfficeBookingFilter = useSelector(
    dashboardDayOfficeBookingFilterSelector
  );
  const userCompanyChoose = useSelector(userCompanyChooseSelector);
  const dashboardFilterDateRange = useSelector(
    dashboardFilterDateRangeSelector
  );
  const filterListPropertySelected = useSelector(
    filterListPropertySelectedSelector
  );
  const [refresh, setRefresh] = useState(false);

  const dashboardPropertyChoose = useSelector(dashboardPropertyChooseSelector);
  const propertyContext = usePermissionCheck(
    UI_ACCESS_TRANSACTION_BY_FILTER_PER
  );

  if (propertyContext) {
    useEffect(() => {
      (async () => {
        await _init();
      })();
    }, [
      dashboardDayOfficeBookingFilter,
      userCompanyChoose,
      dashboardFilterDateRange,
      dashboardPropertyChoose,
      refresh,
      filterListPropertySelected,
    ]);
  } else {
    useEffect(() => {
      (async () => {
        await _init();
      })();
    }, [
      dashboardDayOfficeBookingFilter,
      userCompanyChoose,
      dashboardFilterDateRange,
      refresh,
      filterListPropertySelected,
    ]);
  }

  const _init = async () => {
    try {
      if (dashboardFilterDateRange === null) {
        return;
      }
      setLoading(true);
      await dispatch<any>(
        dashboardDayOfficeBookingPropertyThunk(propertyContext)
      );
    } catch (e) {
      console.log("e", e);
    } finally {
      setLoading(false);
    }
  };

  const dayOfficeField = ({
    day_office_name,
    property_name,
    booking_note,
  }: {
    day_office_name: string;
    property_name: string;
    booking_note?: string;
  }) => (
    <>
      <div className={"table_title"}>{translations.dayOffices.office}:</div>
      <div>{day_office_name}</div>
      <SizeBox height={"10px"} />
      <div className={"table_title"}>{translations.property}:</div>
      <div>{property_name}</div>
      {booking_note ? (
        <>
          <SizeBox height={"10px"} />
          <div className={"table_title"}>{translations.note}:</div>
          <ReadMore>{booking_note}</ReadMore>
        </>
      ) : null}
    </>
  );

  const dateTimeField = ({
    start_time,
    duration,
    created_time,
  }: {
    start_time: number;
    duration: string;
    created_time: string;
  }) => (
    <>
      <div className={"table_title"}>{translations.createdTime}:</div>
      <div>{created_time}</div>
      <SizeBox height={"10px"} />
      <div className={"table_title"}>{translations.startTime}:</div>
      <div>{start_time}</div>
      <SizeBox height={"10px"} />
      <div className={"table_title"}>{translations.duration}:</div>
      <div>{duration}</div>
    </>
  );

  const costField = ({
    hourly_rate,
    total_price,
  }: {
    hourly_rate: string;
    total_price: string;
  }) => (
    <>
      <div className={"table_title"}>{translations.hourlyRate}:</div>
      <div>{hourly_rate}</div>
      <SizeBox height={"10px"} />
      <div className={"table_title"}>{translations.totalPrice}:</div>
      <div>{total_price}</div>
    </>
  );

  const statusField = ({
    status_id,
    refund_amount,
    refund_status_id,
  }: {
    status_id: number;
    refund_amount: string | null;
    refund_status_id: number | null;
  }) => (
    <>
      <div>{translations.roomBookingStatus[status_id]}</div>
      {refund_status_id ? (
        <div>
          ({translations.roomBookingRefundStatus[refund_status_id]}
          {refund_amount ? ` - ${refund_amount}` : ""})
        </div>
      ) : null}
    </>
  );

  const _formatInputData = () => {
    return dashboardDayOfficeBooking.items.map(
      ({
        transaction_id,
        employee_name,
        day_office_name,
        property_name,
        start_time,
        duration,
        hourly_rate,
        total_price,
        refund_amount,
        refund_status_id,
        // note,
        status_id,
        actions,
        id,
        created_time,
        booking_note,
      }: DayOfficeBookingType) => {
        return {
          transaction_id,
          employee_name,
          dayOfficeField: dayOfficeField({
            property_name,
            day_office_name,
            booking_note,
          }),
          dateTimeField: dateTimeField({ created_time, start_time, duration }),
          costField: costField({ hourly_rate, total_price }),
          // note: note || "-",
          statusField: statusField({
            status_id,
            refund_amount,
            refund_status_id,
          }),
          action: (
            <BookingAction
              refresh={refresh}
              setRefresh={setRefresh}
              type={DAY_OFFICE_BOOKING}
              statusId={status_id}
              bookingId={id}
              actions={actions}
              onCallback={(status) =>
                dispatch(
                  dashboardDayOfficeBookingChangeStatusAction({
                    id,
                    statusId: status,
                  })
                )
              }
            />
          ),
        };
      }
    );
  };

  const columns = [
    { key: "day_office_by_transaction_id", col: translations.transactionID },
    { key: "day_office_by_employee_name", col: translations.userName },
    {
      key: "day_office_by_day_office",
      col: translations.dayOffices.dayOfficesName,
    },
    { key: "day_office_by_start_time", col: translations.dateTime },
    { key: "day_office_by_cost", col: translations.revenue },
    // { key: "day_office_by_note", col: translations.note },
    { key: "day_office_by_status", col: translations.status },
    { key: "day_office_by_action", col: translations.action },
  ];

  const _onChangeSortCol = (col: any) => {
    if (dashboardDayOfficeBookingFilter.sort_by !== col.key) {
      dispatch(
        dashboardDayOfficeBookingFilterAction({
          key: "sort_by",
          value: col.key,
        })
      );
      dispatch(
        dashboardDayOfficeBookingFilterAction({
          key: "order_by",
          value: ORDER_BY_ASC,
        })
      );
    } else {
      dispatch(
        dashboardDayOfficeBookingFilterAction({
          key: "sort_by",
          value: col.key,
        })
      );
      if (dashboardDayOfficeBookingFilter.order_by === ORDER_BY_NONE) {
        dispatch(
          dashboardDayOfficeBookingFilterAction({
            key: "order_by",
            value: ORDER_BY_ASC,
          })
        );
      }
      if (dashboardDayOfficeBookingFilter.order_by === ORDER_BY_ASC) {
        dispatch(
          dashboardDayOfficeBookingFilterAction({
            key: "order_by",
            value: ORDER_BY_DESC,
          })
        );
      }
      if (dashboardDayOfficeBookingFilter.order_by === ORDER_BY_DESC) {
        dispatch(
          dashboardDayOfficeBookingFilterAction({
            key: "order_by",
            value: ORDER_BY_NONE,
          })
        );
      }
    }
  };

  return (
    <TableCustom
      orderBy={dashboardDayOfficeBookingFilter.order_by}
      sortBy={dashboardDayOfficeBookingFilter.sort_by}
      sortTable={true}
      onChangeSortCol={_onChangeSortCol}
      tableData={
        dashboardDayOfficeBooking?.items?.length > 0 ? _formatInputData() : []
      }
      columns={columns}
      loading={loading}
      totalItems={dashboardDayOfficeBooking?.pagination?.total}
      currentPage={dashboardDayOfficeBooking?.pagination?.page}
      showTotal
      unit={translations.transactions}
      itemPerPage={dashboardDayOfficeBooking?.pagination?.limit}
      onChangePagination={(e: any, { activePage }: any) => {
        dispatch(
          dashboardDayOfficeBookingFilterAction({
            key: "page",
            value: activePage,
          })
        );
      }}
    />
  );
};

export default TableDayOfficeBookings;
